import { useEffect, useRef, useState } from "react";
import { FetchReadmeProps, UpdateReadmeProps } from "../../../api-client/ReadmeAPIClient";
import Readme from "../../../models/Readme";
import { palette } from "../../../styles/theme/colors";
import Markdown from "markdown-to-jsx";
import { showErrorToast, showSuccessToast } from "../Toasts";
import { Prompt } from "react-router";
import {
  Button,
  ButtonVariant,
  TextField,
  TextFieldVariant,
} from "@merge-api/merge-javascript-shared";
import { Edit, RefreshCcw, Save } from "lucide-react";
import { Spinner } from "@merge-api/merge-javascript-shared";
import { DEFAULT_TEMPLATE } from "./constants";

const TOP_CONTAINER_MAX_HEIGHT = 96;
const TOP_CONTAINER_MIN_HEIGHT = 64;
const TOP_CONTAINER_CONTENT_OVERLAP = TOP_CONTAINER_MAX_HEIGHT - TOP_CONTAINER_MIN_HEIGHT;

interface TryAgainViewProps {
  onClick: () => void;
}

const TryAgainView = ({ onClick }: TryAgainViewProps) => (
  <div className="w-full flex justify-between">
    <span>Failed to load readme</span>
    <Button onClick={onClick} leftIcon={<RefreshCcw size={12} />}>
      Try again
    </Button>
  </div>
);

type ReadmePanelProps<T extends Readme> = {
  fetchReadme: (props: Omit<FetchReadmeProps<T>, "path">) => void;
  updateReadme: (props: Omit<UpdateReadmeProps<T>, "path">) => void;
};

function ReadmePanel<T extends Readme>({ fetchReadme, updateReadme }: ReadmePanelProps<T>) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
  const [text, setText] = useState<string | undefined | null>(null);
  const [editedText, setEditedText] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const titleTextSpanRef = useRef<HTMLSpanElement>(null);

  const fetchAndSetText = () => {
    setIsLoading(true);
    setHasErrorOccurred(false);
    fetchReadme({
      onSuccess: (data) => {
        setIsLoading(false);
        setHasErrorOccurred(false);
        setText(data.text);
      },
      onError: (error) => {
        setIsLoading(false);
        if (error?.status === 404) {
          setHasErrorOccurred(false);
          setText(undefined);
        } else {
          setHasErrorOccurred(true);
        }
      },
    });
  };

  const updateText = (text: string) => {
    setIsLoading(true);
    updateReadme({
      onSuccess: (data) => {
        setIsLoading(false);
        setText(data.text);
        setEditedText(undefined);
        setIsEditing(false);
      },
      onError: () => {
        setIsLoading(false);
        showErrorToast("Failed to save readme.");
      },
      text,
    });
  };

  // We want something like componentDidMount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchAndSetText, []);

  return (
    <div className="px-[72px] w-full overflow-scroll flex flex-col gap-y-6">
      <Prompt
        when={isEditing && text !== editedText}
        message={"Are you sure you want to leave the readme editor? You have unsaved changes."}
      />
      <header
        className="w-full sticky"
        style={{
          height: `${TOP_CONTAINER_MAX_HEIGHT}px`,
          top: `-${TOP_CONTAINER_CONTENT_OVERLAP}px`,
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${palette.border}`,
          background: palette.white,
        }}
      >
        <div
          className="w-full flex items-center justify-between"
          style={{ height: `${TOP_CONTAINER_MIN_HEIGHT}px` }}
        >
          <span ref={titleTextSpanRef} className="font-semibold text-[28px] leading-9">
            Readme
          </span>
          {!isEditing && (text || (!isLoading && !hasErrorOccurred)) && (
            <Button
              onClick={() => {
                setIsEditing(true);
                setEditedText(text ?? DEFAULT_TEMPLATE);
              }}
              leftIcon={<Edit size={16} />}
            >
              Edit
            </Button>
          )}
          {isEditing && (
            <div className="flex flex-row items-center gap-x-3">
              <Button
                variant={ButtonVariant.SecondaryGray}
                onClick={() => {
                  setIsEditing(false);
                  setEditedText(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!editedText}
                onClick={() => (
                  editedText && updateText(editedText), showSuccessToast("Successfully saved!")
                )}
                leftIcon={<Save size={16} />}
                loading={isLoading}
              >
                {isLoading ? "Saving..." : "Save changes"}
              </Button>
            </div>
          )}
        </div>
      </header>
      <div className="flex flex-grow overflow-auto">
        {text === null ? (
          hasErrorOccurred ? (
            <TryAgainView onClick={() => !isLoading && fetchAndSetText()} />
          ) : (
            <Spinner />
          )
        ) : isEditing ? (
          <TextField
            className="w-full m-1"
            autosize
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            rows={5}
            variant={TextFieldVariant.BorderedCode}
          />
        ) : (
          <div className="pb-6 prose prose-sm">
            <Markdown>{text ?? DEFAULT_TEMPLATE}</Markdown>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReadmePanel;
